<script>
import address from '@/modules/mixins/address'
import { getGoogleMapsAPI } from 'gmap-vue'

export default {
    name: 'InputLocation',

    mixins: [address],

    props: {
        value: {
            type: Object,
            default: () => {},
        },
        placeholder: null,
        disabled: Boolean,
        required: Boolean,
        canAddManually: Boolean,
        dropdown: Boolean,
        xs: Boolean,
        sm: Boolean,
        lg: Boolean,
    },

    data() {
        return {
            error: null,
            autocomplete: null,
            result: null,
            top: null,
            open: true,
        }
    },

    computed: {
        google: getGoogleMapsAPI,
        options() {
            return {
                componentRestrictions: {
                    country: this.location,
                },
            }
        },
        showResults() {
            return this.dropdown ? this.open && !!this.result : !!this.result
        },
        isErrorText() {
            return typeof this.error === 'string'
        },
    },

    watch: {
        value: {
            immediate: true,
            deep: true,
            handler(value, valueBefore) {
                if (value.search) this.internalValue = value.search
                if (value.searchResult) this.result = value.searchResult
                if (value?.full_address !== valueBefore?.full_address) {
                    this.internalValue = value?.full_address
                        ? this.removeCountry(value.full_address)
                        : null
                }
            },
        },
    },

    async mounted() {
        if (this.dropdown) {
            this.open = false
            this.top = this.$refs.input.offsetHeight
        }
        await this.$gmapApiPromiseLazy()
        this.autocomplete = new this.google.maps.places.AutocompleteService()
    },

    methods: {
        async onInput({ target: { value } }) {
            this.internalValue = value
            this.error = null
            if (!value.length) {
                this.open = false
                this.result = null
            } else
                this.autocomplete.getPlacePredictions(
                    {
                        input: value,
                        componentRestrictions: {
                            country: this.location,
                        },
                    },
                    (predictions, status) => {
                        if (
                            status != this.google.maps.places.PlacesServiceStatus.OK ||
                            !predictions
                        ) {
                            this.error = true
                            return
                        }
                        if (this.dropdown) this.open = true
                        this.result = predictions
                    }
                )
        },
        clearAddress() {
            this.internalValue = null
            this.result = null
            if (this.dropdown) {
                this.$emit('input', {})
                this.open = false
            }
        },
        async selectAddress(location) {
            try {
                const address = this.convertAddress(
                    this.parseAddress(await this.getAddress(location), false)
                )
                if (address) {
                    if (this.dropdown) {
                        this.internalValue = address.full_address
                        this.open = false
                    }
                    this.$emit(
                        'input',

                        {
                            ...address,
                            ...{
                                search: this.internalValue,
                                searchResult: this.result,
                                sub_address: address.premise,
                            },
                        }
                    )
                }
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e)
                this.error = true
            }
        },
        selectFirst() {
            this.selectAddress(this.result[0])
        },
        addManually() {
            this.$emit('add', {
                search: this.internalValue,
                searchResult: this.result,
            })
        },
    },
}
</script>
<template lang="pug">
.d-flex.flex-column.w-100.position-relative(:class="{'h-100': !dropdown || !result}")
    .form-group.input-group(:class="{'mb-0': dropdown}")            
        input.form-control.mb-0.pl-10(
            ref="input" 
            @input="onInput"
            @keyup.enter="selectFirst"
            v-model="internalValue" 
            :disabled="disabled"
            :placeholder="placeholder"
            :class="{'form-control-xs': xs, 'form-control-sm': sm, 'form-control-lg': lg, 'is-invalid': error, 'pr-9': internalValue, 'border-radius-bottom-left-0 border-radius-bottom-right-0': dropdown && result}")
        .input-group-icon.input-group-icon-prepend
            icon.mt-2.text-cc(icon="fa-solid fa-search")
        transition(name="fade")
            .input-group-icon.clickable(@click="clearAddress" v-if="internalValue")
                icon.mb-1(:icon="`fa-solid ${disabled ? 'fa-lock' : 'fa-times'}`")
    .invalid-feedback(v-if="error && isErrorText" v-html="error")

    slot(v-if="!result || dropdown")

    transition(name="fade" mode="out-in")
        .d-flex.flex-column.border(v-if="showResults" :style="{top: top ? `${top}px` : undefined}" :class="{'position-absolute bg-white w-100 h-fit bottom-0 border-top-0 rounded-bottom z-10': dropdown, 'border-radius-sm': !dropdown}")    
            .d-flex.align-items-center.p-4.cursor-pointer.min-h-fix-75.hover-bg-f3.border-bottom(
                v-for="(location, index) in result"
                @click="selectAddress(location)" 
                role="button" 
                :key="`locationSearch-${location.place_id}`")
                .d-flex.flex-column.justify-content-center
                    span {{ location.structured_formatting.main_text }}
                    span(v-if="location.structured_formatting.secondary_text ") {{ location.structured_formatting.secondary_text }}
                icon.text-primary.ml-auto(icon="fa-solid fa-chevron-right")
            button.btn.btn-add.min-h-fix-75.border-bottom(
                @click="addManually"
                v-if="canAddManually"
                key="addManually"
                role="button")                
                icon(icon="fa-solid fa-plus")
                | {{ $t('details.manual_address') }}
            .flex-center.p-3.google-logo
                span powered by
                img(src="/images/google.png" loading="lazy")
       
        .flex-center.h-100(v-else-if="!result && !dropdown")
            icon.text-cc.mb-4(icon="fa-solid fa-search" size="2xl")
</template>
<style lang="scss">
/* latin */
@font-face {
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url(https://fonts.gstatic.com/s/roboto/v30/KFOlCnqEu92Fr1MmEU9fBBc4.woff2) format('woff2');
    unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304,
        U+0308, U+0329, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF,
        U+FFFD;
}
.google-logo {
    span {
        font-family: Roboto;
        font-style: normal;
        font-weight: 500;
        font-size: 16px;
        line-height: 16px;
        padding: 12px;
        letter-spacing: 0.0575em; /* 0.69px */
        color: #5f6368;
    }
    img {
        height: 28px;
    }
}
</style>
